import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell, LabelList } from 'recharts';
import { Box, Modal, Paper, Grid, Typography, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, esES } from '@mui/x-data-grid';
import { clientAxios8080 } from '../../../../configs/axios';
import {ExportToExcelTicket} from "../../../mesa_servicio_reportes/reportes/export_report/Export_ticket";
import DownloadIcon from "@mui/icons-material/Download";

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  height: 'auto',
  width: 'auto',
};
const columnStyle = {
  margin: '0 1rem',
};

const itemStyle = {
  marginBottom: '1rem',
};

async function getTopTicketsDescComunes() {
  try {
    const response = await clientAxios8080.get('/ms-tickets/serviceLevels/getTopTicketsDescComunes');

    // Definir colores específicos para las barras
    const barColors = [
      '#ff4d4d', // Crítico (rojo)
      '#ff7f50', // Alto (naranja)
      '#ffa500', // Moderado (amarillo-naranja)
      '#ffd700', // Medio (amarillo)
      '#9acd32', // Bajo (verde oliva)
      '#32cd32', // Bajo (verde claro)
      '#66cfb5',
      '#00ced1', // Menor (turquesa)
      '#4682b4', // Menor (azul acero)
      '#0f83c9',
      '#808080'  // Otro (gris)
    ];

    const responseDataTopTicketsDescComunes = response.data.data.map((item, index) => ({
      ...item,
      porcentaje: parseFloat(item.porcentaje).toFixed(2), // Formatear y luego convertir a número
      color: barColors[index % barColors.length],
      cantidad: item.cantidad, // Agregar cantidad
      totalTicketsEnTabla: item.totalTicketsEnTabla,
    }));

    console.log(responseDataTopTicketsDescComunes);

    // Convertir porcentaje a número nuevamente
    responseDataTopTicketsDescComunes.forEach((item) => {
      item.porcentaje = parseFloat(item.porcentaje);
    });

    return responseDataTopTicketsDescComunes;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
}

async function getTopDiezDetalles(descripcion) {
  try {
    const response = await clientAxios8080.get('/ms-tickets/serviceLevels/getTopDiezDetalles');
    const responseData = response.data.data;

    // Filtrar los datos según la descripción
    const filteredData = responseData.filter((item) => item.descripcion === descripcion);

    // Asignar un id único a cada objeto
    const selectedData = filteredData.map((item) => ({
      ticket_id: item.ticket_id,
      casoid: item.casoid,
      usuarioreporta: item.usuarioreporta,
      usuariocargo: item.usuariocargo,
      oficina: item.oficina,
      telusuario: item.telusuario,
      refenlace: item.refenlace,
      numreportesoc: item.numreportesoc,
      numreportebestel: item.numreportebestel,
      numreportecliente: item.numreportecliente,
      responsable: item.responsable,
      ingasignado: item.ingasignado,
      serial: item.serial,
      detalle: item.detalle,
      actividad: item.actividad,
      fc_fechacreacion: item.fc_fechacreacion,
      status: item.status,
      prioridad: item.prioridad,
      severidad: item.severidad,
      descripcion: item.descripcion,
      boafectacion: item.boafectacion,
      boatribuible: item.boatribuible,
      ticketstatus_id: item.ticketstatus_id,
      tipofalla_id: item.tipofalla_id,
      tiporequerimiento_id: item.tiporequerimiento_id,
      tickettipo_id: item.tickettipo_id,
      criticidadticket_id: item.criticidadticket_id,
      sitio_id: item.sitio_id,
      fc_inicioticket: item.fc_inicioticket,
      fc_finticket: item.fc_finticket,
      duracion: item.duracion,
      tiempo_afectacion_minutos: item.tiempo_afectacion_minutos,
      tiempo_afectacion_atribuible: item.tiempo_afectacion_atribuible,
      bst2_tiempo_afectacion_concil: item.bst2_tiempo_afectacion_concil,
      estatus: item.estatus,
      dstipofalla: item.dstipofalla,
      dsrequerimiento: item.dsrequerimiento,
      dstickettipo: item.dstickettipo,
      dscriticidad: item.dscriticidad,
    }));

    return selectedData;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
}

const BreachReasonsChart = () => {
  const [chartData, setChartData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [selectedTicketDetails, setSelectedTicketDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [maxXAxis, setMaxXAxis] = useState(10); // Establece un valor inicial apropiado

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); // Inicia la carga
        const dataResponse = await getTopTicketsDescComunes();
        const data = await getTopDiezDetalles();
        const maxCantidad = Math.max(...dataResponse.map((item) => item.cantidad));

        // Aumentar maxXAxis en un 10%
        const increasedMaxXAxis = maxCantidad + 250;

        setMaxXAxis(increasedMaxXAxis);
        setChartData(dataResponse);
        setLoading(false); // Finaliza la carga
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false); // Finaliza la carga en caso de error
      }
    };

    fetchData();
  }, []);

  const openModal = async (rowData) => {
    setModalOpen(true); // Abrir el modal antes de cargar los detalles
    setSelectedRowData(rowData);

    try {
      setLoading(true); // Inicia la carga
      const detalles = await getTopDiezDetalles(rowData.descripcion);
      setSelectedTicketDetails(detalles);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false); // Finaliza la carga, independientemente del resultado
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const openDetailModal = (rowData) => {
    setSelectedRowData(rowData); // Establece los detalles del ticket seleccionado
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
  };

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart width={400} height={800} layout="vertical" data={chartData}>
          <XAxis
            type="number"
            domain={[0, maxXAxis]}
            tick={{ fontSize: 12, fill: '#ffffff' }}
          />
          <YAxis
            dataKey="descripcion"
            type="category"
            tick={{ fontSize: 12, fill: '#ffffff' }} 
            width={200}
          />
          <Tooltip />
          <Bar dataKey="cantidad" name="Número de tickets" stackId="stack" minPointSize={10}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} onClick={() => openModal(entry)} />
            ))}
            <LabelList dataKey="porcentaje" position="right" formatter={(value) => `${value.toFixed(2)}%`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Modal open={modalOpen} onClose={closeModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '55%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            height: '60%',
            width: '60%',
          }}
        >
          <IconButton
            edge="end"
            color="inherit"
            onClick={closeModal}
            sx={{
              position: 'absolute',
              top: '5px',
              right: '15px',
            }}
          >
            <CloseIcon />
          </IconButton>
          {loading ? (
            <CircularProgress />
          ) : (
            <div style={{ height: 400, width: '100%' }}>
              <DataGrid
                columns={[
                  { field: 'descripcion', headerName: 'Descripción', width: 150 },
                  { field: 'numreportesoc', headerName: 'Número de Reporte SOC', width: 200 },
                  { field: 'numreportebestel', headerName: 'Número de Reporte Bestel', width: 200 },
                  { field: 'responsable', headerName: 'Responsable', width: 150 },
                  { field: 'fc_inicioticket', headerName: 'Inicio del Ticket', width: 200 },
                  { field: 'estatus', headerName: 'Estatus', width: 120 },
                  { field: 'dstipofalla', headerName: 'Tipo de Falla', width: 150 },
                  { field: 'dsrequerimiento', headerName: 'Requerimiento', width: 150 },
                  { field: 'dstickettipo', headerName: 'Tipo de Ticket', width: 150 },
                  { field: 'dscriticidad', headerName: 'Criticidad', width: 150 },
                ]}
                rows={selectedTicketDetails}
                rowsPerPageOptions={[10, 50, 100]}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                getRowId={(row) => row.numreportesoc}
                onRowClick={(params) => openDetailModal(params.row)}
                sx={{
                  width: '100%',
                  '& .MuiDataGrid-row:hover': {
                    background: '#01A9DB',
                    cursor: 'pointer',
                  },
                }}
              />
            </div>
          )}
        </Box>
      </Modal>

      <Modal
        open={detailModalOpen}
        onClose={closeDetailModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box display="flex" justifyContent="center" alignItems="center" sx={modalStyle}>
          <Paper
            sx={{
              p: 2,
              position: 'relative',
              overflowY: 'auto', // Agrega desplazamiento vertical si es necesario
            }}
          >
            <IconButton
              edge="end"
              color="inherit"
              onClick={closeDetailModal}
              sx={{
                position: 'absolute',
                top: '5px',
                right: '15px',
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6">Detalle del ticket</Typography>
            <Grid container spacing={2}>
              <Grid item xs={5} style={columnStyle}>
                <div style={itemStyle}>
                  <strong>Ticket Id:</strong>
                  {selectedRowData.ticket_id}
                </div>
                <div style={itemStyle}>
                  <strong>Caso Id:</strong>
                  {selectedRowData.casoid}
                </div>
                <div style={itemStyle}>
                  <strong>Usuario que reporta:</strong>
                  {selectedRowData.usuarioreporta}
                </div>
                <div style={itemStyle}>
                  <strong>Usuario cargo:</strong>
                  {selectedRowData.usuariocargo}
                </div>
                <div style={itemStyle}>
                  <strong>Oficina:</strong>
                  {selectedRowData.oficina}
                </div>
                <div style={itemStyle}>
                  <strong>Teléfono usuario:</strong>
                  {selectedRowData.telusuario}
                </div>
                <div style={itemStyle}>
                  <strong>Referencia enlace:</strong>
                  {selectedRowData.refenlace}
                </div>
                <div style={itemStyle}>
                  <strong>Número reporte SOC:</strong>
                  {selectedRowData.numreportesoc}
                </div>
                <div style={itemStyle}>
                  <strong>Número reporte Bestel:</strong>
                  {selectedRowData.numreportebestel}
                </div>
                <div style={itemStyle}>
                  <strong>Número reporte cliente:</strong>
                  {selectedRowData.numreportecliente}
                </div>
                <div style={itemStyle}>
                  <strong>Responsable:</strong>
                  {selectedRowData.responsable}
                </div>
                <div style={itemStyle}>
                  <strong>Ingeniero asignado:</strong>
                  {selectedRowData.ingasignado}
                </div>
                <div style={itemStyle}>
                  <strong>Serial:</strong>
                  {selectedRowData.serial}
                </div>
                <div style={itemStyle}>
                  <strong>Actividad:</strong>
                  {selectedRowData.actividad}
                </div>
                <div style={itemStyle}>
                  <strong>Día:</strong>
                  {selectedRowData.dia}
                </div>
                <div style={itemStyle}>
                  <strong>Prioridad:</strong>
                  {selectedRowData.prioridad}
                </div>
                <div style={itemStyle}>
                  <strong>Severidad:</strong>
                  {selectedRowData.severidad}
                </div>
                <div style={itemStyle}>
                  <strong>Descripción:</strong>
                  {selectedRowData.descripcion}
                </div>
                <div style={itemStyle}>
                  <strong>Afectación:</strong>
                  {selectedRowData.boafectacion ? 'Sí' : 'No'}
                </div>
                <div style={itemStyle}>
                  <strong>Atribuible:</strong>
                  {selectedRowData.boatribuible ? 'Sí' : 'No'}
                </div>
              </Grid>
              <Grid item xs={5} style={columnStyle}>
                <div style={itemStyle}>
                  <strong>Detalle:</strong>
                  {selectedRowData.detalle}
                </div>
                <div style={itemStyle}>
                  <strong>Fecha de Inicio del Ticket:</strong>
                  {selectedRowData.fc_inicioticket}
                </div>
                <div style={itemStyle}>
                  <strong>Fecha de Fin del Ticket:</strong>
                  {selectedRowData.fc_finticket}
                </div>
                <div style={itemStyle}>
                  <strong>Duración:</strong>
                  {selectedRowData.duracion}
                </div>
                <div style={itemStyle}>
                  <strong>Tiempo de Afectación (minutos):</strong>
                  {selectedRowData.tiempo_afectacion_minutos}
                </div>
                <div style={itemStyle}>
                  <strong>Tiempo Atribuible de Afectación:</strong>
                  {selectedRowData.tiempo_afectacion_atribuible}
                </div>
                <div style={itemStyle}>
                  <strong>Estatus:</strong>
                  {selectedRowData.estatus}
                </div>
                <div style={itemStyle}>
                  <strong>Tipo de Falla:</strong>
                  {selectedRowData.dstipofalla}
                </div>
                <div style={itemStyle}>
                  <strong>Requerimiento:</strong>
                  {selectedRowData.dsrequerimiento}
                </div>
                <div style={itemStyle}>
                  <strong>Tipo de Ticket:</strong>
                  {selectedRowData.dstickettipo}
                </div>
                <div style={itemStyle}>
                  <strong>Criticidad:</strong>
                  {selectedRowData.dscriticidad}
                </div>
              </Grid>
            </Grid>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton  onClick={() => ExportToExcelTicket(selectedRowData)}>
                <DownloadIcon/>
              </IconButton>
              Exportar a Excel
            </div>
          </Paper>
        </Box>
      </Modal>
    </>
  );
};

export default BreachReasonsChart;
